import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { api, apiFetch } from './client'

export interface DashboardKpis {
  open_orders: number
  low_stock: number
  return_rate_30d: number
  orders_total: number
}

export interface DashboardShipmentStates {
  arrived?: number
  shipped?: number
  packed?: number
  paid?: number
  open?: number
  cancelled?: number
}

export interface DashboardTopProduct {
  sku: string
  name: string
  qty: number
  pct: number
}

export interface DashboardAlert {
  sku: string
  name: string
  stock: number
  quantity_alert: number
  level: 'critical' | 'low'
}

export interface DashboardPayload {
  kpis: DashboardKpis
  orders_30d: number[]
  shipment_states: DashboardShipmentStates
  top_products: DashboardTopProduct[]
  alerts: DashboardAlert[]
  generated_at: string
}

export function useDashboard() {
  return useQuery({
    queryKey: ['system', 'dashboard'],
    queryFn: () => api.get<DashboardPayload>('/system/dashboard/'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

export const TIMESERIES_METRICS = ['orders', 'revenue', 'returns', 'units'] as const
export type TimeseriesMetric = (typeof TIMESERIES_METRICS)[number]
export const TIMESERIES_RANGES = ['1M', '2M', '3M', '6M', '12M', '24M', '36M'] as const
export type TimeseriesRange = (typeof TIMESERIES_RANGES)[number]
export interface TimeseriesPoint { label: string; value: number; weekend: boolean }
export interface TimeseriesPayload {
  metric: TimeseriesMetric; range: TimeseriesRange; unit: 'day' | 'week' | 'month'
  points: TimeseriesPoint[]; sum: number; avg: number
}
// ============================================================
//  Task 13: API-Hooks fuer das neue Dashboard-Modul (Backend: src/modules/dashboard/).
//  Typen sind 1:1 aus src/modules/dashboard/service.ts + logic.ts abgeleitet (Feldnamen,
//  Optionalitaet, snake_case wo das Backend snake_case liefert). Alle Query-Keys beginnen
//  mit 'dashboard', damit sie gemeinsam per invalidateQueries({queryKey:['dashboard']})
//  ungueltig gemacht werden koennen (Rename-Lieferant/Vormerkungs-Mutationen unten).
// ============================================================

// Range-Presets — Spiegel von DASH_RANGES (src/modules/dashboard/logic.ts). Gilt fuer
// /series/, /summary/, /top/, /article-series/.
export const DASH_RANGES = ['1T', '7T', '30T', '90T', '6M', '12M', '24M', '36M'] as const
export type DashRange = (typeof DASH_RANGES)[number]

// Kanal-Whitelist — Spiegel von CHANNELS (src/modules/dashboard/platform-map.ts).
export const DASH_CHANNELS = ['Amazon', 'eBay', 'Shopify', 'Metro', 'Kaufland', 'Sonstige'] as const
export type DashChannel = (typeof DASH_CHANNELS)[number]

// ------------------------------------------------------------
//  GET /dashboard/series/ — Umsatz/Gutschriften (Order-Ebene) bzw. Menge/Abgang-EK
//  (Positions-Ebene, Grundartikel-umgeschluesselt). Spiegel von SeriesPayload (service.ts).
// ------------------------------------------------------------
export const DASH_SERIES_METRICS = ['revenue', 'credits', 'units', 'abgang_ek'] as const
export type DashSeriesMetric = (typeof DASH_SERIES_METRICS)[number]

export interface DashSeriesBucket { label: string; values: Record<string, number> }
export interface DashSeriesPayload {
  metric: string
  range: string
  unit: 'day' | 'week' | 'month'
  channels: string[]
  buckets: DashSeriesBucket[]
  generated_at: string
}

export function useDashSeries(metric: DashSeriesMetric, range: DashRange, drill = false) {
  return useQuery({
    queryKey: ['dashboard', 'series', metric, range, drill],
    queryFn: () => api.get<DashSeriesPayload>(
      `/dashboard/series/?metric=${metric}&range=${range}${drill ? '&drill=amazon' : ''}`,
    ),
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------
//  GET /dashboard/summary/ — KPI-Kacheln. Spiegel von SummaryPayload (service.ts).
// ------------------------------------------------------------
export interface DashSummary {
  revenue: number
  credits: number
  credit_rate: number
  orders_count: number
  credits_count: number
  stock_value_ek: number
  ga_count: number
  ga_without_ek: number
  generated_at: string
}

export function useDashSummary(range: DashRange) {
  return useQuery({
    queryKey: ['dashboard', 'summary', range],
    queryFn: () => api.get<DashSummary>(`/dashboard/summary/?range=${range}`),
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------
//  GET /dashboard/top/ — Top-Grundartikel | Top-Kategorien (Drilldown via `parent`) |
//  Top-Retouren (optional `channel`). Spiegel von TopPayload/TopRow (service.ts).
// ------------------------------------------------------------
// W3 (#9): Spiegel von RETURNS_RATE_MIN_SOLD (src/modules/dashboard/service.ts). Wird hier
// NUR fuer den Erklaertext genutzt — die Schwelle selbst entscheidet das Backend (es liefert
// returnRate=null darunter). Bei einer Aenderung im Backend diesen Wert mitziehen; ein Drift
// macht den Hinweistext falsch, nicht die Zahlen.
export const RETURNS_RATE_MIN_SOLD = 10

export const TOP_DIMS = ['ga', 'category', 'returns'] as const
export type TopDim = (typeof TOP_DIMS)[number]
export type TopMetric = 'qty' | 'revenue'

export interface TopRow {
  id: number | null
  label: string
  sublabel: string
  qty: number
  value: number
  pct: number
  // W3 (#9): nur bei dim='returns' befuellt, sonst null. Wire-Format-Pruefung (Lehre PR #44):
  // die SQL-Spalten heissen sold_qty/return_rate, aber foldTopRows() im Backend uebersetzt
  // sie in camelCase, und die Route reicht das Payload unveraendert durch — hier steht
  // deshalb camelCase, exakt wie das Backend-Interface TopRow (service.ts).
  soldQty: number | null
  returnRate: number | null
}
export interface TopPayload {
  dim: TopDim
  metric: TopMetric
  rows: TopRow[]
  generated_at: string
}

export function useTop(
  dim: TopDim, range: DashRange, metric: TopMetric,
  parent?: number | null, channel?: DashChannel | null,
) {
  const params = new URLSearchParams({ dim, range, metric })
  if (parent !== undefined && parent !== null) params.set('parent', String(parent))
  if (channel !== undefined && channel !== null) params.set('channel', channel)
  return useQuery({
    queryKey: ['dashboard', 'top', dim, range, metric, parent ?? null, channel ?? null],
    queryFn: () => api.get<TopPayload>(`/dashboard/top/?${params.toString()}`),
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------
//  GET /dashboard/ga-search/ — Grundartikel-Suche fuer die Artikel-Auswahl der
//  Artikel-Analyse-Seite. Spiegel von GaSearchPayload/GaSearchItem (service.ts). Feuert
//  bewusst NICHT bei leerem Suchtext (TanStack `enabled`, keine leere Server-Anfrage).
// ------------------------------------------------------------
export interface GaSearchItem {
  ausverkauf?: boolean
  ausverkauf_quelle?: 'dr' | 'hand' | null
  id: number
  sku: string
  merchant_sku: string | null
  name: string
  quantity: number
}
export interface GaSearchPayload {
  items: GaSearchItem[]
  generated_at: string
}

export function useGaSearch(q: string) {
  const term = q.trim()
  return useQuery({
    queryKey: ['dashboard', 'ga-search', term],
    queryFn: () => api.get<GaSearchPayload>(`/dashboard/ga-search/?q=${encodeURIComponent(term)}`),
    enabled: term.length > 0,
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------
//  GET /dashboard/article-series/ — Verkaufsentwicklung eines gewaehlten Grundartikels
//  je Kanal. Spiegel von ArticleSeriesPayload (service.ts). Feuert bewusst NICHT ohne
//  gewaehlten Artikel (`gaId` null/undefined) — analog useGaSearch oben.
// ------------------------------------------------------------
export interface ArticleSeriesGa {
  id: number
  sku: string
  merchant_sku: string | null
  name: string
}
export interface ArticleSeriesBucket {
  label: string
  values: Record<string, { qty: number; revenue: number }>
}
export interface ArticleSeriesPayload {
  ga: ArticleSeriesGa
  unit: 'day' | 'week' | 'month'
  channels: string[]
  buckets: ArticleSeriesBucket[]
  generated_at: string
}

export function useArticleSeries(gaId: number | null | undefined, range: DashRange) {
  return useQuery({
    queryKey: ['dashboard', 'article-series', gaId ?? null, range],
    queryFn: () => api.get<ArticleSeriesPayload>(`/dashboard/article-series/?ga=${gaId}&range=${range}`),
    enabled: gaId !== null && gaId !== undefined && gaId > 0,
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------
//  GET /dashboard/reach/ — Lager-Reichweiten-Tabelle. Spiegel von ReachPayload/ReachRow/
//  ReachOpenNote (service.ts). Kein Cache (analog Backend-Route) — jeder Parameter-Wechsel
//  (Filter, Blaetterung) ist ein eigener Query-Key.
// ------------------------------------------------------------
export const REACH_WINDOWS = ['3M', '6M', '12M', '24M'] as const
export type ReachWindow = (typeof REACH_WINDOWS)[number]

export interface ReachParams {
  window?: ReachWindow
  supplier?: number | 'none' | null
  exclude_ordered?: boolean
  // GELÖSCHT*/#AUSVERKAUF-Artikel einblenden (Backend blendet sie per Default aus).
  include_dead?: boolean
  max_months?: number | null
  category?: number | null
  q?: string | null
  limit?: number
  offset?: number
  // Korrektur #13: Massen-Abfrage — Nummern-Liste (SKU/Sternnummer/Lagernummer).
  skus?: string[] | null
}

export interface ReachOpenNote {
  order_number: string
  quantity: number | null
  created_at: string
}

/** Modul 38 / B3 — offene Bestellung des Einkaufsmoduls zu diesem Artikel.
 *  Spiegel von ReachOpenPurchase im Backend (dashboard/service.ts). VERTRAG eingefroren
 *  2026-08-17; die Felder haengen erst mit B3 an ReachRow. Anzeige in der Spalte
 *  "Offene Bestellungen": Menge · Nr (+n weitere) · ETA. */
export interface ReachOpenPurchase {
  order_id: number
  order_number: string
  /** Auswahl der aeltesten Bestellung IMMER ueber seq, nie ueber order_number (text). */
  seq: number
  qty_open: number
  eta: string | null
  warnungen: Array<"over_delivered" | "unattributed_receipt">
}
export interface ReachBausatzTeil {
  ga_id: number
  sku: string
  name: string
  menge: number
  quantity: number
  supplier_id: number | null
  supplier_name: string | null
}

export interface ReachRow {
  ausverkauf?: boolean
  ausverkauf_quelle?: 'dr' | 'hand' | null
  ga_id: number
  dr_stock_id: string | null
  sku: string
  merchant_sku: string | null
  name: string
  supplier_id: number | null
  supplier_name: string | null
  sold_window: number
  avg_month: number
  bausatz_teile: ReachBausatzTeil[]
  quantity: number
  reach_months: number | null
  stock_value_ek: number
  open_notes: ReachOpenNote[]
  /** Modul 38 B3: offene Bestellungen des Einkaufsmoduls, aelteste zuerst (nach seq). */
  open_purchases: ReachOpenPurchase[]
  /** Summe der offenen Mengen ueber alle Bestellungen dieses Artikels. */
  open_purchase_qty: number
  /** Plan 2026-10-26: Reichweite mit Bestand + offenen Bestellungen + offenen Vormerkungen. */
  reach_with_ordered_months: number | null
  /** Fehlmenge bis zum Ziel (`target_months`) nach Abzug von Bestand und offener Menge; 0 = genug. */
  shortfall_qty: number
  /** Bestand ist vor der naechsten Ankunft leer. */
  eta_gap: boolean
  /** Naechste Ankunft einer offenen Bestellung (YYYY-MM-DD). */
  next_eta: string | null
}
export interface ReachPayload {
  window_months: number
  /** Ziel fuer „genug bestellt": max_months, ohne Filter 6 Monate. */
  target_months: number
  rows: ReachRow[]
  total: number
  generated_at: string
}

export function useReach(params: ReachParams) {
  const {
    window: windowParam, supplier, exclude_ordered: excludeOrdered, include_dead: includeDead,
    max_months: maxMonths, category, q, limit, offset, skus,
  } = params
  const qs = new URLSearchParams()
  if (windowParam !== undefined) qs.set('window', windowParam)
  if (supplier !== undefined && supplier !== null) qs.set('supplier', String(supplier))
  if (excludeOrdered) qs.set('exclude_ordered', '1')
  if (includeDead) qs.set('include_dead', '1')
  if (maxMonths !== undefined && maxMonths !== null) qs.set('max_months', String(maxMonths))
  if (category !== undefined && category !== null) qs.set('category', String(category))
  if (q !== undefined && q !== null && q.trim() !== '') qs.set('q', q.trim())
  if (limit !== undefined) qs.set('limit', String(limit))
  if (offset !== undefined) qs.set('offset', String(offset))
  if (skus !== undefined && skus !== null && skus.length > 0) qs.set('skus', skus.join(','))
  return useQuery({
    queryKey: [
      'dashboard', 'reach', windowParam ?? null, supplier ?? null, Boolean(excludeOrdered),
      Boolean(includeDead),
      maxMonths ?? null, category ?? null, q?.trim() ?? null, limit ?? null, offset ?? null,
      skus?.join(',') ?? null,
    ],
    queryFn: () => api.get<ReachPayload>(`/dashboard/reach/?${qs.toString()}`),
    staleTime: 60_000, refetchOnWindowFocus: false,
    // KEIN automatischer Wiederholungslauf — anders als der globale Standard in `main.tsx`
    // (`failureCount < 2`, also drei Versuche).
    //
    // Diese eine Abfrage ist teuer: gesund rund 22 s, am 2026-08-18 unter Last 98–152 s bis
    // zum 500 (OI-149). Mit drei Versuchen plus Wartezeit dazwischen erschien der Fehler
    // erst nach etwa sechs Minuten — bis dahin stand auf der Seite „Lädt…". Genau das war
    // das Fehlerbild, das der Nutzer als „hängt" gemeldet hat.
    //
    // Zwei Gründe gegen die Wiederholung, nicht nur einer: sie verzögert die Auskunft, und
    // sie schickt dieselbe teure Abfrage ein zweites und drittes Mal gegen einen Server, der
    // gerade nicht kann. Die Wiederholung gehört dem Nutzer — der Knopf „Erneut versuchen"
    // steht im Fehlerbild.
    retry: false,
  })
}

// ------------------------------------------------------------
//  GET /dashboard/suppliers/ + PATCH /dashboard/suppliers/:id/ — Lieferanten-Verwaltung.
//  Spiegel von SuppliersPayload/SupplierListRow (GET) bzw. SupplierBase (PATCH-Antwort,
//  service.ts) — die PATCH-Antwort liefert bewusst KEIN article_count (das ist eine
//  reine Zaehl-Projektion der GET-Liste), daher `Omit<Supplier, 'article_count'>`.
// ------------------------------------------------------------
export interface Supplier {
  id: number
  dr_supplier_id: string | null
  name: string
  source: 'dreamrobot' | 'manual'
  is_active: boolean
  article_count: number
}
export interface SuppliersPayload { suppliers: Supplier[] }

export function useSuppliers() {
  return useQuery({
    queryKey: ['dashboard', 'suppliers'],
    queryFn: () => api.get<SuppliersPayload>('/dashboard/suppliers/'),
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

// Abschluss-Review, IMPORTANT-Finding: GET /dashboard/suppliers/ ist seit dem Fix rein
// lesend — der frueher darin versteckte DR-Lieferanten-ID-Abgleich (Lazy-Sync, schrieb in
// die DB) ist jetzt ein eigener Schreib-Endpunkt POST /dashboard/suppliers/sync/
// (Scope stock:write). InventoryReach.tsx ruft diese Mutation beim Laden der Seite
// best-effort auf (kein Toast bei Fehler/403 — ein Nutzer mit reinem Lese-Scope sieht die
// Seite weiterhin voll funktionsfaehig, nur eben ohne ganz neue DR-Lieferanten-IDs) und
// invalidiert bei Erfolg die Lieferantenliste, damit neu angelegte Eintraege sofort
// erscheinen.
export function useSyncSuppliers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ synced: number }>('/dashboard/suppliers/sync/', { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dashboard', 'suppliers'] }),
  })
}

export interface SupplierPatchBody {
  name?: string
  is_active?: boolean
}

// Mutation: Lieferant umbenennen/aktiv setzen. Invalidiert den gesamten 'dashboard'-
// Query-Key-Bereich (nicht nur ['dashboard','suppliers']), weil der Lieferantenname auch
// in /reach/ (supplier_name je Zeile) angezeigt wird.
export function useRenameSupplier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: SupplierPatchBody }) =>
      apiFetch<{ supplier: Omit<Supplier, 'article_count'> }>(`/dashboard/suppliers/${id}/`, {
        method: 'PATCH', body: JSON.stringify(patch),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dashboard'] }),
  })
}

// ------------------------------------------------------------
//  GET/POST /dashboard/purchase-notes/ + POST /dashboard/purchase-notes/:id/status/ —
//  Bestell-Vormerkungen. Spiegel von PurchaseNoteBase/PurchaseNoteListRow (GET-Liste)
//  bzw. PurchaseNoteBase (status-Antwort, service.ts).
// ------------------------------------------------------------
export const PURCHASE_NOTE_STATUSES = ['open', 'received', 'cancelled'] as const
export type PurchaseNoteStatus = (typeof PURCHASE_NOTE_STATUSES)[number]
export type PurchaseNoteCloseStatus = Extract<PurchaseNoteStatus, 'received' | 'cancelled'>

export interface PurchaseNoteBase {
  id: number
  stock_item_id: number
  order_number: string
  quantity: number | null
  note: string | null
  status: PurchaseNoteStatus
  created_by: string | null
  created_at: string
  received_at: string | null
}
export interface PurchaseNote extends PurchaseNoteBase {
  sku: string
  merchant_sku: string | null
  name: string
}
export interface PurchaseNotesPayload { notes: PurchaseNote[] }

// GET /dashboard/purchase-notes/ — nicht in der Task-13-Vorgabenliste der Hooks
// namentlich aufgefuehrt, aber der Endpunkt gehoert zum konsumierten Set (Tasks 4-11) und
// liefert die einzige Quelle fuer die Vormerkungs-`id` (das eingebettete `open_notes` in
// ReachRow hat KEINE id, siehe ReachOpenNote oben — Status-Mutation braucht die id also
// von hier). Ohne diesen Hook koennten Tasks 15-17 offene Vormerkungen nicht abschliessen/
// stornieren.
export function usePurchaseNotes(status?: PurchaseNoteStatus | null) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return useQuery({
    queryKey: ['dashboard', 'purchase-notes', status ?? null],
    queryFn: () => api.get<PurchaseNotesPayload>(`/dashboard/purchase-notes/${qs}`),
    staleTime: 60_000, refetchOnWindowFocus: false,
  })
}

export interface CreatePurchaseNotesBody {
  stock_item_ids: number[]
  order_number: string
  quantity?: number | null
  note?: string | null
}

export function useCreatePurchaseNotes() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePurchaseNotesBody) =>
      api.post<{ created: number }>('/dashboard/purchase-notes/', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dashboard'] }),
  })
}

export function useSetNoteStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: PurchaseNoteCloseStatus }) =>
      api.post<{ note: PurchaseNoteBase }>(`/dashboard/purchase-notes/${id}/status/`, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dashboard'] }),
  })
}

// ------------------------------------------------------------
// Welle 2 (#2): Lagerwert-Historie (Tages-Snapshots).
// ------------------------------------------------------------
export interface StockValueSnapshot {
  snapshot_date: string
  total_value_ek: number
  total_quantity: number
  ga_count: number
  ga_without_ek: number
}
export interface StockValueHistoryPayload { rows: StockValueSnapshot[] }

export function useStockValueHistory(range: DashRange) {
  return useQuery({
    queryKey: ['dashboard', 'stock-value-history', range],
    queryFn: () => api.get<StockValueHistoryPayload>(`/dashboard/stock-value-history/?range=${range}`),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

// ------------------------------------------------------------
// Welle 2 (#15b): Artikel-Notizen (rein informativ, append-only).
// ------------------------------------------------------------
export interface StockItemNote {
  id: number
  stock_item_id: number
  note: string
  created_by: string | null
  created_at: string
}
export interface StockItemNotesPayload { notes: StockItemNote[] }

export function useStockItemNotes(stockItemId: number | null) {
  return useQuery({
    queryKey: ['dashboard', 'stock-notes', stockItemId],
    queryFn: () => api.get<StockItemNotesPayload>(`/dashboard/stock-notes/?stock_item_id=${stockItemId}`),
    enabled: stockItemId !== null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}

export function useCreateStockItemNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { stock_item_id: number; note: string }) =>
      api.post<{ note: StockItemNote }>('/dashboard/stock-notes/', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dashboard', 'stock-notes'] }),
  })
}


// ------------------------------------------------------------
//  POST /dashboard/ga-sales/ — Katalog-Spalte „Verkauf 30 T.“
//  Verkaufte Stueck je Grundartikel-Familie (Storno ausgeschlossen, Sets aufgeloest,
//  Gutschriften negativ, alle Kanaele). buckets: 12 Abschnitte à 5 Tage, aeltester zuerst;
//  0–5 = die 30 Tage davor, 6–11 = die letzten 30 Tage.
// ------------------------------------------------------------
export type GaSalesTrend = 'up' | 'down' | 'flat' | 'none'
export interface GaSalesItem {
  id: number
  sold_30d: number
  sold_prev_30d: number
  trend: GaSalesTrend
  change_pct: number | null
  buckets: number[]
}
export interface GaSalesPayload {
  window_days: number
  bucket_days: number
  items: GaSalesItem[]
  generated_at: string
}

// Ein Sammelaufruf fuer die sichtbaren Katalogzeilen. ids sortiert im Key, damit dieselbe
// Seite in anderer Sortierung (Suche) den Cache trifft.
export function useGaSales(ids: number[]) {
  const unique = [...new Set(ids)].sort((a, b) => a - b)
  return useQuery({
    queryKey: ['dashboard', 'ga-sales', unique],
    queryFn: () => api.post<GaSalesPayload>('/dashboard/ga-sales/', { ids: unique }),
    enabled: unique.length > 0,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // Beim Ordner-/Suchwechsel behalten bekannte Zeilen ihre Werte, bis die neue Seite da ist.
    placeholderData: keepPreviousData,
  })
}
